import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, TextField, MenuItem, Select, Chip, Paper, IconButton,
  CircularProgress, Tooltip, Collapse, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatIcon from '@mui/icons-material/Chat';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

const API = process.env.REACT_APP_API_URL || '/api';

// Mapeos predefinidos
const HANDLERS = [
  { value: 'image', label: 'Análisis de imagen', icon: '📷' },
  { value: 'sintomas', label: 'Síntomas', icon: '🩺' },
  { value: 'conversacion', label: 'Conversación general', icon: '💬' },
  { value: 'informacion', label: 'Información (KB)', icon: 'ℹ️' },
  { value: 'tool_use', label: 'Tool use (citas)', icon: '🛠️' },
  { value: 'saludo', label: 'Saludo', icon: '👋' },
];

const TIPOS_ERROR = [
  { value: 'falto_ofrecer_cita', label: 'Faltó ofrecer cita', color: '#dc2626' },
  { value: 'respuesta_repetitiva', label: 'Respuesta repetitiva', color: '#f59e0b' },
  { value: 'info_incorrecta', label: 'Información incorrecta', color: '#ef4444' },
  { value: 'falta_cierre', label: 'Falta de cierre', color: '#6366f1' },
  { value: 'otro', label: 'Otro', color: '#64748b' },
];

const RESULTADO_COLORS = {
  completada: '#10b981',
  timeout: '#f59e0b',
  escalada: '#3b82f6',
  rechazada: '#dc2626',
};

export default function AdminCorreccionesConversacionales() {
  const userId = localStorage.getItem('user_id');

  const [loading, setLoading] = useState(false);
  const [conversaciones, setConversaciones] = useState([]);
  const [filtroResultado, setFiltroResultado] = useState('');
  const [convSeleccionada, setConvSeleccionada] = useState(null);
  const [detalleConv, setDetalleConv] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const [correcciones, setCorrecciones] = useState([]);
  const [verCorrecciones, setVerCorrecciones] = useState(false);

  const [dialogAbierto, setDialogAbierto] = useState(false);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [form, setForm] = useState({
    handler: 'image',
    tipo_error: 'falto_ofrecer_cita',
    descripcion_disparador: '',
    accion_correctiva: '',
    chatbot_id_global: false,
  });

  // ==================== FETCHERS ====================
  const fetchConversaciones = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ user_id: userId, limit: '50' });
      if (filtroResultado) params.set('resultado', filtroResultado);
      const r = await fetch(`${API}/admin/corrections/conversaciones?${params}`);
      if (r.ok) {
        const data = await r.json();
        setConversaciones(data.conversaciones || []);
      }
    } catch {}
    setLoading(false);
  }, [userId, filtroResultado]);

  const fetchDetalle = useCallback(async (convId) => {
    if (!userId || !convId) return;
    setCargandoDetalle(true);
    try {
      const r = await fetch(`${API}/admin/corrections/conversaciones/${convId}?user_id=${userId}`);
      if (r.ok) setDetalleConv(await r.json());
    } catch {}
    setCargandoDetalle(false);
  }, [userId]);

  const fetchCorrecciones = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await fetch(`${API}/admin/corrections/correcciones-conversacionales?user_id=${userId}`);
      if (r.ok) setCorrecciones(await r.json());
    } catch {}
  }, [userId]);

  useEffect(() => { fetchConversaciones(); }, [fetchConversaciones]);
  useEffect(() => { fetchCorrecciones(); }, [fetchCorrecciones]);

  // ==================== ACCIONES ====================
  const seleccionarConv = (conv) => {
    setConvSeleccionada(conv);
    fetchDetalle(conv.id);
  };

  // Mapeo: tipo de error → handler default + acción correctiva sugerida
  const TIPO_DEFAULTS = {
    falto_ofrecer_cita: {
      handler: 'image',
      accion: '💚 ¿Quieres que te muestre los horarios disponibles para agendar una valoración?',
    },
    respuesta_repetitiva: {
      handler: 'image',
      accion: 'Reformular la respuesta sin repetir el mismo template.',
    },
    info_incorrecta: {
      handler: 'informacion',
      accion: 'Aclarar que la información correcta es: [completar].',
    },
    falta_cierre: {
      handler: 'conversacion',
      accion: '¿Hay algo más en lo que pueda ayudarte? 😊',
    },
    otro: {
      handler: 'image',
      accion: '',
    },
  };

  const abrirDialogMarcar = (idx, msgAsistente, msgPaciente, tipoPredef = 'falto_ofrecer_cita') => {
    setTurnoSeleccionado({ idx, msgAsistente, msgPaciente });
    const defaults = TIPO_DEFAULTS[tipoPredef] || TIPO_DEFAULTS.otro;
    setForm({
      handler: defaults.handler,
      tipo_error: tipoPredef,
      descripcion_disparador: msgPaciente
        ? `El paciente dijo: "${msgPaciente.substring(0, 120)}" — y el asistente respondió sin cerrar el círculo.`
        : '',
      accion_correctiva: defaults.accion,
      chatbot_id_global: false,
    });
    setDialogAbierto(true);
  };

  const guardarCorreccion = async () => {
    try {
      const payload = {
        chatbot_id: form.chatbot_id_global ? null : (detalleConv?.chatbot_id || null),
        handler: form.handler,
        tipo_error: form.tipo_error,
        descripcion_disparador: form.descripcion_disparador.trim(),
        accion_correctiva: form.accion_correctiva.trim(),
        conversacion_log_id: detalleConv?.id,
        turn_index: turnoSeleccionado?.idx,
        contexto_ejemplo: turnoSeleccionado ? {
          paciente: turnoSeleccionado.msgPaciente?.substring(0, 500),
          asistente: turnoSeleccionado.msgAsistente?.substring(0, 500),
        } : null,
      };
      const r = await fetch(`${API}/admin/corrections/correcciones-conversacionales?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        setDialogAbierto(false);
        fetchCorrecciones();
        fetchConversaciones();
      }
    } catch {}
  };

  const toggleCorr = async (id, nuevaActiva) => {
    try {
      await fetch(`${API}/admin/corrections/correcciones-conversacionales/${id}?user_id=${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activa: nuevaActiva }),
      });
      fetchCorrecciones();
    } catch {}
  };

  const eliminarCorr = async (id) => {
    if (!window.confirm('¿Eliminar esta corrección?')) return;
    try {
      await fetch(`${API}/admin/corrections/correcciones-conversacionales/${id}?user_id=${userId}`, {
        method: 'DELETE',
      });
      fetchCorrecciones();
    } catch {}
  };

  // ==================== RENDER ====================
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Alert severity="info" icon={<LightbulbIcon />} sx={{ mb: 2, borderRadius: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.3 }}>
          Cómo funciona la revisión conversacional
        </Typography>
        <Typography sx={{ fontSize: 12 }}>
          Click en cualquier respuesta de NARA → marcá qué le faltó hacer →
          la próxima vez que un caso similar ocurra, NARA anexa la acción correctiva automáticamente.
          Diferente de las "Sugerencias" automáticas — acá vos decidís el patrón.
        </Typography>
      </Alert>

      {/* Correcciones activas (colapsable) */}
      <Paper elevation={0} sx={{
        p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #e2e8f0',
        bgcolor: '#fafbff',
      }}>
        <Box
          onClick={() => setVerCorrecciones((v) => !v)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
        >
          <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b', flex: 1 }}>
            Correcciones activas ({correcciones.filter(c => c.activa).length})
          </Typography>
          {verCorrecciones ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={verCorrecciones}>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {correcciones.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', py: 1 }}>
                No hay correcciones aún. Marca un turno problemático abajo para crear la primera.
              </Typography>
            ) : (
              correcciones.map((c) => {
                const tipo = TIPOS_ERROR.find(t => t.value === c.tipo_error);
                return (
                  <Box key={c.id} sx={{
                    p: 1.2, borderRadius: 1.5,
                    bgcolor: c.activa ? '#fff' : '#f1f5f9',
                    border: `1px solid ${c.activa ? '#cbd5e1' : '#e2e8f0'}`,
                    opacity: c.activa ? 1 : 0.6,
                    display: 'flex', alignItems: 'flex-start', gap: 1,
                  }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.4, flexWrap: 'wrap' }}>
                        <Chip label={c.handler} size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 600 }} />
                        {tipo && (
                          <Chip label={tipo.label} size="small" sx={{ height: 18, fontSize: 10, bgcolor: tipo.color, color: '#fff', fontWeight: 600 }} />
                        )}
                        {c.chatbot_id ? (
                          <Chip label={`Chatbot ${c.chatbot_id}`} size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#f1f5f9', color: '#64748b' }} />
                        ) : (
                          <Chip label="Global" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }} />
                        )}
                        {c.veces_aplicada > 0 && (
                          <Typography sx={{ fontSize: 10, color: '#10b981', fontWeight: 600 }}>
                            ×{c.veces_aplicada} aplicada{c.veces_aplicada === 1 ? '' : 's'}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 12, color: '#475569', mb: 0.3 }}>
                        <b>Si:</b> {c.descripcion_disparador}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#1e3a5f', fontWeight: 500 }}>
                        <b>Anexar:</b> {c.accion_correctiva}
                      </Typography>
                    </Box>
                    <Tooltip title={c.activa ? 'Desactivar' : 'Activar'}>
                      <IconButton size="small" onClick={() => toggleCorr(c.id, !c.activa)} sx={{ color: c.activa ? '#10b981' : '#94a3b8' }}>
                        {c.activa ? <ToggleOnIcon /> : <ToggleOffIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => eliminarCorr(c.id)} sx={{ color: '#dc2626' }}>
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Two-pane: lista + viewer */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
        {/* LEFT: Lista de conversaciones */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Select
              size="small" value={filtroResultado} displayEmpty
              onChange={(e) => setFiltroResultado(e.target.value)}
              sx={{ flex: 1, fontSize: 12 }}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="completada">Completadas</MenuItem>
              <MenuItem value="timeout">Timeout</MenuItem>
              <MenuItem value="escalada">Escaladas</MenuItem>
            </Select>
            <IconButton size="small" onClick={fetchConversaciones}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Paper elevation={0} sx={{
            border: '1px solid #e2e8f0', borderRadius: 2,
            maxHeight: 600, overflowY: 'auto',
          }}>
            {loading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : conversaciones.length === 0 ? (
              <Typography sx={{ p: 2, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                Sin conversaciones
              </Typography>
            ) : (
              conversaciones.map((c) => {
                const fecha = c.inicio ? new Date(c.inicio).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
                const sel = convSeleccionada?.id === c.id;
                const color = RESULTADO_COLORS[c.resultado] || '#64748b';
                return (
                  <Box
                    key={c.id}
                    onClick={() => seleccionarConv(c)}
                    sx={{
                      p: 1.2,
                      cursor: 'pointer',
                      borderBottom: '1px solid #f1f5f9',
                      bgcolor: sel ? '#eff6ff' : 'transparent',
                      borderLeft: sel ? '3px solid #3b82f6' : '3px solid transparent',
                      '&:hover': { bgcolor: sel ? '#dbeafe' : '#f8fafc' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#1e293b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.nombre_paciente || c.numero_telefono}
                      </Typography>
                      {c.tiene_correccion && (
                        <Tooltip title="Tiene corrección creada">
                          <CheckCircleIcon sx={{ fontSize: 13, color: '#10b981' }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                      {c.chatbot_name || `Chatbot ${c.chatbot_id}`} · {c.total_mensajes} msgs · {fecha}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Paper>
        </Box>

        {/* RIGHT: Viewer */}
        <Paper elevation={0} sx={{
          border: '1px solid #e2e8f0', borderRadius: 2,
          minHeight: 400, maxHeight: 700, overflowY: 'auto', p: 0,
        }}>
          {!convSeleccionada ? (
            <Box sx={{ p: 6, textAlign: 'center', color: '#94a3b8' }}>
              <ChatIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
              <Typography sx={{ fontSize: 13 }}>
                Seleccioná una conversación para revisarla
              </Typography>
            </Box>
          ) : cargandoDetalle ? (
            <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          ) : detalleConv ? (
            <Box sx={{ p: 2 }}>
              <Box sx={{ pb: 1.5, mb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>
                  {detalleConv.nombre_paciente || detalleConv.numero_telefono}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  {detalleConv.chatbot_name || `Chatbot ${detalleConv.chatbot_id}`} ·
                  {' '}{(detalleConv.mensajes || []).length} mensajes
                </Typography>
              </Box>

              {(detalleConv.mensajes || []).map((m, idx) => {
                const isUser = m.role === 'user';
                const msgAnterior = idx > 0 ? detalleConv.mensajes[idx - 1] : null;
                const msgPaciente = !isUser && msgAnterior?.role === 'user' ? msgAnterior.content : null;

                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex', gap: 1, mb: 1.2,
                      flexDirection: isUser ? 'row' : 'row-reverse',
                    }}
                  >
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '50%',
                      bgcolor: isUser ? '#dbeafe' : '#f3e8ff',
                      color: isUser ? '#1e40af' : '#7c3aed',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {isUser ? <PersonIcon sx={{ fontSize: 16 }} /> : <SmartToyIcon sx={{ fontSize: 16 }} />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{
                        bgcolor: isUser ? '#f1f5f9' : '#faf5ff',
                        border: `1px solid ${isUser ? '#cbd5e1' : '#e9d5ff'}`,
                        borderRadius: 2,
                        p: 1.2,
                      }}>
                        <Typography sx={{
                          fontSize: 12, color: '#1e293b',
                          whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                        }}>
                          {m.content || '(vacío)'}
                        </Typography>
                      </Box>
                      {/* Action bar: solo en respuestas de NARA — botones de marcar */}
                      {!isUser && (
                        <Box sx={{
                          mt: 0.6, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center',
                        }}>
                          <Typography sx={{
                            fontSize: 10, fontWeight: 700, color: '#94a3b8',
                            textTransform: 'uppercase', letterSpacing: 0.6, mr: 0.3,
                          }}>
                            Marcar:
                          </Typography>
                          {[
                            { tipo: 'falto_ofrecer_cita', label: 'Faltó cita', color: '#dc2626' },
                            { tipo: 'respuesta_repetitiva', label: 'Repetitiva', color: '#f59e0b' },
                            { tipo: 'info_incorrecta', label: 'Info incorrecta', color: '#ef4444' },
                            { tipo: 'falta_cierre', label: 'Falta cierre', color: '#6366f1' },
                            { tipo: 'otro', label: 'Otro…', color: '#64748b' },
                          ].map((b) => (
                            <Box
                              key={b.tipo}
                              onClick={() => abrirDialogMarcar(idx, m.content, msgPaciente, b.tipo)}
                              sx={{
                                px: 0.9, py: 0.25,
                                borderRadius: 999,
                                fontSize: 10.5, fontWeight: 600,
                                cursor: 'pointer',
                                bgcolor: '#fff',
                                color: b.color,
                                border: `1px solid ${b.color}`,
                                transition: 'all 0.15s',
                                '&:hover': {
                                  bgcolor: b.color,
                                  color: '#fff',
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 2px 6px ${b.color}40`,
                                },
                              }}
                            >
                              {b.label}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : null}
        </Paper>
      </Box>

      {/* Dialog para crear corrección */}
      <Dialog open={dialogAbierto} onClose={() => setDialogAbierto(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          color: '#fff', py: 1.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditNoteIcon />
            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
              Marcar turno · Crear corrección
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          {turnoSeleccionado && (
            <Box sx={{
              p: 1.2, mb: 2, borderRadius: 1.5,
              bgcolor: '#fafbff', border: '1px solid #e2e8f0',
            }}>
              {turnoSeleccionado.msgPaciente && (
                <>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', mb: 0.3 }}>
                    Paciente dijo
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#475569', mb: 1, fontStyle: 'italic' }}>
                    "{turnoSeleccionado.msgPaciente.substring(0, 200)}{turnoSeleccionado.msgPaciente.length > 200 ? '...' : ''}"
                  </Typography>
                </>
              )}
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', mb: 0.3 }}>
                NARA respondió
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#1e293b' }}>
                {turnoSeleccionado.msgAsistente?.substring(0, 200)}{turnoSeleccionado.msgAsistente?.length > 200 ? '...' : ''}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Handler</InputLabel>
              <Select
                value={form.handler}
                onChange={(e) => setForm({ ...form, handler: e.target.value })}
                label="Handler"
              >
                {HANDLERS.map(h => (
                  <MenuItem key={h.value} value={h.value}>{h.icon} {h.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Tipo de error</InputLabel>
              <Select
                value={form.tipo_error}
                onChange={(e) => setForm({ ...form, tipo_error: e.target.value })}
                label="Tipo de error"
              >
                {TIPOS_ERROR.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            fullWidth size="small" multiline rows={3}
            label="¿Cuándo se debe disparar esta corrección?"
            placeholder="Ej: cuando el paciente envía una imagen y en el caption pide ser operado..."
            value={form.descripcion_disparador}
            onChange={(e) => setForm({ ...form, descripcion_disparador: e.target.value })}
            sx={{ mb: 1.5 }}
            helperText="Describí el patrón en lenguaje natural — el LLM lo usa para detectar casos similares"
          />

          <TextField
            fullWidth size="small" multiline rows={3}
            label="Acción correctiva (texto a anexar a la respuesta)"
            placeholder="Ej: ¿Querés que te muestre los horarios disponibles?"
            value={form.accion_correctiva}
            onChange={(e) => setForm({ ...form, accion_correctiva: e.target.value })}
            sx={{ mb: 1 }}
            helperText="Este texto se anexará al final de la respuesta del handler cuando el patrón se detecte"
          />

          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            p: 1.2, mt: 1, borderRadius: 1.5,
            bgcolor: form.chatbot_id_global ? '#fef3c7' : '#f1f5f9',
            border: `1px solid ${form.chatbot_id_global ? '#fbbf24' : '#e2e8f0'}`,
            cursor: 'pointer',
          }}
            onClick={() => setForm({ ...form, chatbot_id_global: !form.chatbot_id_global })}
          >
            <input
              type="checkbox" checked={form.chatbot_id_global}
              onChange={(e) => setForm({ ...form, chatbot_id_global: e.target.checked })}
            />
            <Typography sx={{ fontSize: 12, color: '#1e293b', flex: 1 }}>
              Aplicar a <b>todos los chatbots</b> (no solo este)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setDialogAbierto(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={guardarCorreccion}
            disabled={!form.descripcion_disparador.trim() || !form.accion_correctiva.trim()}
            sx={{
              textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
            }}
          >
            Crear corrección
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
